/**
 * Captured-error ring buffer for the debug bridge.
 *
 * Subscribes to the `stream:debug:error` window CustomEvent dispatched by
 * `apps/stream/src/lib/logger.ts` (window.onerror, unhandledrejection) and
 * `apps/stream/src/shared/components/ErrorBoundary.tsx` (componentDidCatch).
 * The bridge serves the buffer via `/debug/state.captured_errors` and
 * `/debug/reset` clears it.
 *
 * Bounded to `BUFFER_SIZE` entries (oldest dropped). The decoupled
 * custom-event channel keeps the logger and ErrorBoundary agnostic of the
 * bridge.
 */

import type { CapturedError } from './types'

const BUFFER_SIZE = 50

/** The ring buffer. Module-private; access via the exported helpers below. */
let buffer: CapturedError[] = []

/** Append an entry to the buffer, dropping the oldest entry once full. */
export function pushError(entry: CapturedError): void {
  buffer.push(entry)
  if (buffer.length > BUFFER_SIZE) {
    // Drop oldest. Slice rather than shift to avoid mutating shared snapshot
    // arrays returned by `snapshotErrors()`.
    buffer = buffer.slice(buffer.length - BUFFER_SIZE)
  }
}

/** Return a defensive copy of the buffer. */
export function snapshotErrors(): CapturedError[] {
  return [...buffer]
}

/** Empty the buffer. Called from the `reset` op. */
export function clearErrors(): void {
  buffer = []
}

/**
 * Install the window listener that pipes `stream:debug:error` CustomEvents
 * into the buffer. Returns the cleanup function so the bridge's `useEffect`
 * teardown can detach it.
 */
export function installErrorBufferListener(): () => void {
  if (typeof window === 'undefined') {
    // SSR / non-DOM environment — no-op cleanup.
    return () => {}
  }
  const handler = (event: Event) => {
    const detail = (event as CustomEvent<CapturedError>).detail
    if (!detail || typeof detail !== 'object') return
    pushError(detail)
  }
  window.addEventListener('stream:debug:error', handler)
  return () => window.removeEventListener('stream:debug:error', handler)
}
